import React from "react";
import cx from "classnames";
import { bool, func, oneOf, string, node, shape, instanceOf } from "prop-types";
import Icon from "../../assets/icons/Icon";
import Spinner from "../Spinner/Spinner";
import styles from "./Button.module.scss";

/**
 * @returns A button component, with various variants available. Pass an icon to the component using either iconLeft or iconRight prop.
 *
 */

const Button = React.forwardRef((props, refProp) => {
  const {
    children,
    className,
    href,
    element: Element = href ? "a" : "button",
    iconLeft,
    iconRight,
    isDisabled,
    isLoading,
    loadingText,
    size,
    type,
    variant,
    ...restProps
  } = props;
  const classNames = cx(
    styles.button,
    {
      [styles.filled]: variant === "filled",
      [styles.outline]: variant === "outline",
      [styles.ghost]: variant === "ghost",
      [styles.white]: variant === "white",
      [styles.lg]: size === "lg",
      [styles.md]: size === "md",
      [styles.sm]: size === "sm",
    },
    className
  );

  return (
    <Element
      disabled={isDisabled || isLoading}
      data-loading={isLoading ? true : undefined}
      href={href}
      className={classNames}
      ref={refProp}
      type={!href && type ? type : undefined}
      {...restProps}
    >
      {iconLeft && !isLoading && (
        <Icon icon={iconLeft} className={styles.iconLeft} />
      )}

      {isLoading && <Spinner size="xs" />}

      {isLoading
        ? (loadingText && (
            <span className={styles.loading}>{loadingText}</span>
          )) || <span className={styles.hiddenLoading}>Loading...</span>
        : children}

      {iconRight && !isLoading && (
        <Icon icon={iconRight} className={styles.iconRight} />
      )}
    </Element>
  );
});

export default Button;

Button.propTypes = {
  /** Content for the button */
  children: node.isRequired,
  /** For CSS customisation */
  className: string,
  /** Used to change what HTML tag is rendered */
  element: oneOf(["button", "a"]),
  /** Set the URL of the link. Also returns a link element. */
  href: string,
  /** Name of the icon as a string to render on the left */
  iconLeft: string,
  /** Name of the icon as a string to render on the right */
  iconRight: string,
  /** Boolean to render button as disabled */
  isDisabled: bool,
  /** Set the button state to loading to display a spinner */
  isLoading: bool,
  /** Text to display when button is in loading state */
  loadingText: string,
  /** Function to run on click */
  onClick: func,
  /** The ref to pass through to the input with React.forwardRef. Useful
   * for focusing buttons.
   */
  refProp: shape({
    current: instanceOf(Element),
  }),
  /** Determine the size of the button */
  size: oneOf(["lg", "md", "sm"]),
  /** Determine the type of button */
  type: oneOf(["button", "submit"]),
  /** Determine the variant of the button */
  variant: oneOf(["filled", "outline", "white", "ghost"]),
};

Button.defaultProps = {
  isDisabled: false,
  size: "lg",
  type: "button",
  variant: "filled",
};
